export const environments = ["development", "staging", "production"] as const;
export type AppEnvironment = (typeof environments)[number];

export const featureFlags = [
  "PUBLIC_CHAT",
  "AI_CHAT",
  "FACEBOOK_LOGIN",
  "GOOGLE_LOGIN",
  "EMAIL_LINK_LOGIN",
  "CLOVER",
  "QR_CODES",
] as const;
export type FeatureFlag = (typeof featureFlags)[number];

export interface RuntimeConfig {
  readonly environment: AppEnvironment;
  readonly timezone: "Pacific/Honolulu";
  readonly dataClassification: "WELLNESS_ONLY";
  readonly publicSiteUrl: string;
  readonly portalUrl: string;
  readonly apiUrl: string;
  readonly allowedOrigins: readonly string[];
  readonly features: Readonly<Record<FeatureFlag, boolean>>;
}

export function baseConfig(environment: AppEnvironment): RuntimeConfig {
  return {
    environment,
    timezone: "Pacific/Honolulu",
    dataClassification: "WELLNESS_ONLY",
    publicSiteUrl: "https://whitehavenwellness.com",
    portalUrl: "https://portal.whitehavenwellness.com",
    apiUrl: "https://api.whitehavenwellness.com",
    allowedOrigins: [
      "https://whitehavenwellness.com",
      "https://portal.whitehavenwellness.com",
    ],
    features: {
      PUBLIC_CHAT: false,
      AI_CHAT: false,
      FACEBOOK_LOGIN: true,
      GOOGLE_LOGIN: true,
      EMAIL_LINK_LOGIN: true,
      CLOVER: false,
      QR_CODES: true,
    },
  };
}

export const developmentConfig = {
  ...baseConfig("development"),
  publicSiteUrl: "http://localhost:3000",
  portalUrl: "http://localhost:3001",
  apiUrl: "http://localhost:8787",
  allowedOrigins: ["http://localhost:3000", "http://localhost:3001"],
  features: {
    ...baseConfig("development").features,
    CLOVER: false,
    QR_CODES: false,
  },
} as const satisfies RuntimeConfig;

export const stagingConfig = {
  ...baseConfig("staging"),
  publicSiteUrl: "https://staging.whitehavenwellness.com",
  portalUrl: "https://staging-portal.whitehavenwellness.com",
  apiUrl: "https://staging-api.whitehavenwellness.com",
  allowedOrigins: [
    "https://staging.whitehavenwellness.com",
    "https://staging-portal.whitehavenwellness.com",
  ],
} as const satisfies RuntimeConfig;

export const productionConfig = baseConfig("production");

export function configForEnvironment(
  environment: AppEnvironment,
): RuntimeConfig {
  if (environment === "development") return developmentConfig;
  if (environment === "staging") return stagingConfig;
  return productionConfig;
}
